/**
 * Run many async tasks at once, but only up to `maxAtOnce` at the same time.
 * @template T
 * @template R
 * @param {T[]} items
 * @param {number} maxAtOnce
 * @param {(item: T, index: number) => Promise<R>} task
 * @returns {Promise<R[]>}
 */
export async function runInParallel(items, maxAtOnce, task) {
  if (items.length === 0) return [];

  const limit = Math.max(1, Math.min(maxAtOnce, items.length));
  const results = new Array(items.length);
  let nextIndex = 0;
  let firstError = null;

  async function worker() {
    while (!firstError) {
      const i = nextIndex++;
      if (i >= items.length) return;
      try {
        results[i] = await task(items[i], i);
      } catch (e) {
        firstError = e;
        return;
      }
    }
  }

  const workers = [];
  for (let w = 0; w < limit; w++) {
    workers.push(worker());
  }
  for (const w of workers) {
    await w;
  }

  if (firstError) {
    throw firstError;
  }
  return results;
}
